import {
  ChangeEventHandler, FocusEventHandler, memo, useCallback, useEffect, useMemo, useState,
} from 'react';
import { DateRange, SelectRangeEventHandler } from 'react-day-picker';
import { ClickAwayListener } from '@mui/material';
import DateRangePicker from '../DefaultDateRangePicker';
import { IDateRangePickerWithTextFieldProps } from '../..';
import Grid from '../../../Grid';
import TextField from '../../../TextField';
import IconButton from '../../../IconButton';
import Button from '../../../Button';
import Popper from '../../../Popper';
import PopperTitle from '../../../Popper/components/PopperTitle';
import PopperContent from '../../../Popper/components/PopperContent';
import { CloseIcon } from '../../../../icons';
import { S6 } from '../../../../theme';
import {
  DEFAULT_MIN_DATE,
  formatDateFromJSDate,
  isDateBeforeDate,
  isDateValid,
  setEndOfDay,
  setStartOfDay,
} from '../../../../services/dateTimeFormatter';
import usePopper from '../../../../hooks/usePopper';
import DateSelectTextField, {
  type TDateSelectTextFieldProps,
} from '../../../TextField/DateSelectTextField';

const getTextFieldValue = (
  from: string,
  to: string,
  selectLabelText: IDateRangePickerWithTextFieldProps['selectLabelText'],
): string | undefined => (from ? `${from} - ${to}` : selectLabelText);

const DateRangePickerWithTextField = (props: IDateRangePickerWithTextFieldProps) => {
  const {
    onSubmit,
    onClear,
    t,
    labelFromText = t('dateRangePicker.from'),
    labelToText = t('dateRangePicker.to'),
    cancelButtonText = t('dateRangePicker.clearAll'),
    submitButtonText = t('dateRangePicker.show'),
    dateInvalidText = t('dateRangePicker.dateIsInvalid'),
    from,
    to,
    selectLabelText = t('dateRangePicker.selectLabelText'),
    popperTitle = t('dateRangePicker.popperTitle'),
    fullWidth = false,
    placement,
    minDate: preMinDate = DEFAULT_MIN_DATE,
    maxDate: preMaxDate,
  } = props;

  const [selectedRange, setSelectedRange] = useState<DateRange>(
    () => ((from && to) ? { from, to } : { from: undefined, to: undefined }),
  );
  const [fromValue, setFromValue] = useState<string>(
    () => ((from && to) ? formatDateFromJSDate(from) : ''),
  );
  const [toValue, setToValue] = useState<string>(
    () => ((from && to) ? formatDateFromJSDate(to) : ''),
  );

  const [textFieldValue, setTextFieldValue] = useState<string | undefined>(
    () => getTextFieldValue(fromValue, toValue, selectLabelText),
  );
  const { isOpen, anchorEl, setPopperIsOpen } = usePopper();
  const handleTextFieldClick = useCallback<
    NonNullable<TDateSelectTextFieldProps['onClick']>
  >(
    (event) => setPopperIsOpen(!isOpen, event),
    [setPopperIsOpen, isOpen],
  );
  const handlePopperClose = useCallback(() => {
    setPopperIsOpen(false);
    if (!fromValue || !toValue) {
      setTextFieldValue(selectLabelText);
      onSubmit({ from: undefined, to: undefined });
    }
  }, [setPopperIsOpen, fromValue, toValue, selectLabelText, onSubmit]);
  const isSubmitDisabled = useMemo(
    () => !isDateValid(selectedRange.from) || !isDateValid(selectedRange.to),
    [selectedRange.from, selectedRange.to],
  );

  const handleFromChange: ChangeEventHandler<HTMLInputElement> = useCallback((e) => {
    setFromValue(e.target.value);
    const date = new Date(e.target.value);
    setSelectedRange({ from: date, to: selectedRange.to });
  }, [selectedRange.to]);
  const handleFromBlur: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement> = useCallback((
    e,
  ) => {
    const date = new Date(e.target.value);
    if (selectedRange.to && isDateBeforeDate(selectedRange.to, date)) {
      setSelectedRange({ from: selectedRange.to, to: date });
      setToValue(formatDateFromJSDate(date));
      setFromValue(formatDateFromJSDate(selectedRange.to));
    }
  }, [selectedRange.to]);
  const handleToChange: ChangeEventHandler<HTMLInputElement> = useCallback((e) => {
    setToValue(e.target.value);
    const date = new Date(e.target.value);
    setSelectedRange({ from: selectedRange.from, to: date });
  }, [selectedRange.from]);
  const handleToBlur: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement> = useCallback((
    e,
  ) => {
    const date = new Date(e.target.value);
    if (selectedRange.from && isDateBeforeDate(date, selectedRange.from)) {
      setSelectedRange({ from: date, to: selectedRange.from });
      setToValue(formatDateFromJSDate(selectedRange.from));
      setFromValue(formatDateFromJSDate(date));
    }
  }, [selectedRange.from]);
  const handleRangeSelect: SelectRangeEventHandler = useCallback((range: DateRange | undefined) => {
    const processedRange = range ?? { from: undefined, to: undefined };
    setSelectedRange(processedRange);
    setFromValue(formatDateFromJSDate(processedRange.from));
    setToValue(formatDateFromJSDate(processedRange.to));
  }, []);

  const clearFromValue = useCallback(() => {
    setFromValue('');
    setSelectedRange((prev) => ({ ...prev, from: undefined }));
  }, []);
  const clearToValue = useCallback(() => {
    setToValue('');
    setSelectedRange((prev) => ({ ...prev, to: undefined }));
  }, []);
  const clearAll = useCallback(() => {
    clearFromValue();
    clearToValue();
    setTextFieldValue(selectLabelText);
    onClear({ from: undefined, to: undefined });
  }, [onClear, clearFromValue, clearToValue, selectLabelText]);

  const minDate = useMemo(() => setStartOfDay(preMinDate), [preMinDate]);
  const maxDate = useMemo(() => setEndOfDay(preMaxDate ?? new Date()), [preMaxDate]);
  const isFromDateInvalid = useMemo(
    () => (!!selectedRange.from && !isDateValid(selectedRange.from))
      || (!!selectedRange.from && selectedRange.from < minDate),
    [selectedRange.from, minDate],
  );
  const isToDateInvalid = useMemo(
    () => (!!selectedRange.to && !isDateValid(selectedRange.to))
      || (!!selectedRange.to && selectedRange.to > maxDate),
    [selectedRange.to, maxDate],
  );

  const handleSubmit = useCallback(() => {
    if (isFromDateInvalid || isToDateInvalid) return;
    setTextFieldValue(`${fromValue} - ${toValue}`);
    onSubmit({ from: selectedRange.from, to: setEndOfDay(selectedRange.to!) });
    setPopperIsOpen(false);
  }, [
    onSubmit,
    selectedRange,
    fromValue,
    toValue,
    setPopperIsOpen,
    isFromDateInvalid,
    isToDateInvalid,
  ]);

  useEffect(() => {
    if (!from || !to) {
      setSelectedRange({ from: undefined, to: undefined });
      setFromValue('');
      setToValue('');
      setTextFieldValue(selectLabelText);
      return;
    }
    const newFromValue = formatDateFromJSDate(from);
    const newToValue = formatDateFromJSDate(to);
    setSelectedRange({ from, to });
    setFromValue(newFromValue);
    setToValue(newToValue);
    setTextFieldValue(getTextFieldValue(newFromValue, newToValue, selectLabelText));
  }, [from, to, selectLabelText]);

  return (
    <ClickAwayListener onClickAway={handlePopperClose}>
      <div>
        <DateSelectTextField
          value={textFieldValue}
          onClick={handleTextFieldClick}
          open={isOpen}
          fullWidth={fullWidth}
        />
        <Popper open={isOpen} anchorEl={anchorEl} placement={placement}>
          <PopperTitle color={S6} onClose={handlePopperClose}>
            {popperTitle}
          </PopperTitle>
          <PopperContent>
            <Grid container flexWrap="nowrap" spacing={3}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label={labelFromText}
                  value={fromValue}
                  onChange={handleFromChange}
                  onBlur={handleFromBlur}
                  InputProps={{
                    endAdornment: !!fromValue && (
                      <IconButton onClick={clearFromValue}><CloseIcon /></IconButton>
                    ),
                  }}
                  helperText={isFromDateInvalid && dateInvalidText}
                  error={isFromDateInvalid}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label={labelToText}
                  value={toValue}
                  onChange={handleToChange}
                  onBlur={handleToBlur}
                  InputProps={{
                    endAdornment: !!toValue && (
                      <IconButton onClick={clearToValue}><CloseIcon /></IconButton>
                    ),
                  }}
                  helperText={isToDateInvalid && dateInvalidText}
                  error={isToDateInvalid}
                />
              </Grid>
            </Grid>
            <DateRangePicker
              selectedRange={selectedRange}
              onRangeSelect={handleRangeSelect}
              minDate={minDate}
              maxDate={maxDate}
            />
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Button fullWidth variant="outlined" onClick={clearAll}>{cancelButtonText}</Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  disabled={isSubmitDisabled}
                  onClick={handleSubmit}
                >
                  {submitButtonText}
                </Button>
              </Grid>
            </Grid>
          </PopperContent>
        </Popper>
      </div>
    </ClickAwayListener>
  );
};

export default memo(DateRangePickerWithTextField);
